/**
 * Форматирование величин, которые встречаются в нескольких экранах.
 *
 * Функции вызываются во время рендера, но React-контекста у них нет, поэтому
 * перевод берётся из общего экземпляра i18next. При смене языка компоненты
 * перерисовываются, и значения пересчитываются вместе с ними.
 */
import i18next from "i18next";

import { serverNow } from "./serverClock";

/** Локаль для группировки разрядов: «1 234» против «1,234». */
function locale(): string {
  return i18next.resolvedLanguage === "ru" ? "ru-RU" : "en-US";
}

/** Год: отрицательные значения — это «до н. э.». */
export function formatYear(year: number): string {
  return year < 0 ? i18next.t("format.bc", { year: Math.abs(year) }) : String(year);
}

/** Расстояние: у близких промахов важны метры, у дальних — тысячи километров. */
export function formatDistance(km: number): string {
  if (!Number.isFinite(km)) return "—";
  if (km < 1) return i18next.t("format.meters", { value: Math.round(km * 1000) });
  if (km < 100) return i18next.t("format.km", { value: km.toFixed(1) });
  return i18next.t("format.km", { value: Math.round(km).toLocaleString(locale()) });
}

/** Разница в годах со склонением. */
export function formatYearDiff(diff: number): string {
  if (diff === 0) return i18next.t("format.exactYear");
  return i18next.t("format.years", { count: diff });
}

export function formatScore(score: number): string {
  return score.toLocaleString(locale());
}

export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export function formatDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale(), { day: "numeric", month: "long", year: "numeric" });
}

/** Дата со временем: «10 октября, 22:31». */
export function formatDateTime(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(locale(), { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
}

/**
 * Оставшиеся секунды до дедлайна раунда.
 *
 * Дедлайн — момент по часам сервера, и сравнивать его можно только с ними же:
 * часы телефона нередко спешат на минуты, и раунд кончался, не начавшись.
 */
export function secondsUntil(deadline?: string | null): number | null {
  if (!deadline) return null;
  const target = new Date(deadline).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.round((target - serverNow()) / 1000));
}

export function formatClock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}
